/**
 * Prompt Ceiling — web mirror of the mobile promptCeiling utility.
 *
 * Same API and same PROMPT_RULES as the mobile version
 * (`apps/mobile/lib/feedback/promptCeiling.ts`), but uses:
 *
 *  - `localStorage`   for cooldown timestamps and once-ever flags
 *                     (persists across browser sessions)
 *  - `sessionStorage` for the per-session modal counter
 *                     (auto-clears when the tab is closed — perfect
 *                     session boundary on web)
 *
 * Inline prompts (style === "inline") are always allowed and do NOT
 * count against the session ceiling.
 */

import { safeSessionGet, safeSessionRemove, safeSessionSet } from "@/lib/browser-storage";

// ---------------------------------------------------------------------------
// Types (mirror of mobile)
// ---------------------------------------------------------------------------

export type PromptStyle = "inline" | "modal";

export interface PromptRule {
  source: string;
  /**
   * 0  = no cooldown
   * -1 = once-ever
   * N  = N-day cooldown window
   */
  cooldownDays: number;
  /** 0 = unlimited, 1 = once per session */
  perSession: number;
  style: PromptStyle;
}

// ---------------------------------------------------------------------------
// Rule table — keep in sync with mobile version
// ---------------------------------------------------------------------------

export const PROMPT_RULES: Record<string, PromptRule> = {
  chat_message_thumbs: {
    source: "chat_message_thumbs",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  chat_session_stars: {
    source: "chat_session_stars",
    cooldownDays: 0,
    perSession: 1,
    style: "modal",
  },
  paid_report: {
    source: "paid_report",
    cooldownDays: 0,
    perSession: 1,
    style: "modal",
  },
  free_kundli: {
    source: "free_kundli",
    cooldownDays: 90,
    perSession: 1,
    style: "modal",
  },
  daily_rashifal_thumbs: {
    source: "daily_rashifal_thumbs",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  daily_rashifal: {
    source: "daily_rashifal",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  in_app_nps: {
    source: "in_app_nps",
    cooldownDays: 180,
    perSession: 1,
    style: "modal",
  },
  app_rating_prompt: {
    source: "app_rating_prompt",
    cooldownDays: 365,
    perSession: 1,
    style: "modal",
  },
  intent_onboarding: {
    source: "intent_onboarding",
    cooldownDays: -1,
    perSession: 1,
    style: "modal",
  },
  seo_page_thumb: {
    source: "seo_page_thumb",
    cooldownDays: -1,
    perSession: 0,
    style: "inline",
  },
  razorpay_postpayment: {
    source: "razorpay_postpayment",
    cooldownDays: 0,
    perSession: 1,
    style: "modal",
  },
  web_chat_message: {
    source: "web_chat_message",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  tool_result: {
    source: "tool_result",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  explainable_thumb: {
    source: "explainable_thumb",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  /**
   * The Send-feedback dialog (FloatingFeedback). The user opens it themselves,
   * so it is not an interruption and must never be blanked by the session
   * ceiling another prompt consumed (C04).
   */
  in_app_form: {
    source: "in_app_form",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  /** "Was this helpful?" on editorial pages (ContentPageFeedback) — inline. */
  content_page: {
    source: "content_page",
    cooldownDays: 0,
    perSession: 0,
    style: "inline",
  },
  exit_intent: {
    source: "exit_intent",
    cooldownDays: 30,
    perSession: 1,
    style: "modal",
  },
  /** Catch-all default for unknown sources. */
  intent_postpayment_followup: {
    source: "intent_postpayment_followup",
    cooldownDays: 0,
    perSession: 1,
    style: "modal",
  },
};

// ---------------------------------------------------------------------------
// Storage key helpers
// ---------------------------------------------------------------------------

const NS = "feedback.ceiling";

function lastShownKey(source: string): string {
  return `${NS}.lastShown:${source}`;
}

function everShownKey(source: string): string {
  return `${NS}.everShown:${source}`;
}

const SESSION_MODALS_KEY = `${NS}.session.modalsShown`;

// ---------------------------------------------------------------------------
// sessionStorage helpers (modal counter — resets on tab close)
// ---------------------------------------------------------------------------

function getSessionModalCount(): number {
  const raw = safeSessionGet(SESSION_MODALS_KEY);
  if (!raw) return 0;
  try {
    return JSON.parse(raw) as number;
  } catch {
    return 0;
  }
}

function incrementSessionModalCount(): void {
  safeSessionSet(SESSION_MODALS_KEY, JSON.stringify(getSessionModalCount() + 1));
}

// ---------------------------------------------------------------------------
// localStorage helpers (cooldown + once-ever — persists across sessions)
// ---------------------------------------------------------------------------

function lsGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function lsSet(key: string, value: unknown): void {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Ignore storage errors
  }
}

// ---------------------------------------------------------------------------
// Core API
// ---------------------------------------------------------------------------

/**
 * Returns true when the prompt for `source` is allowed to appear.
 *
 * Decision order:
 *  1. Resolve rule (unknown → intent_postpayment_followup default).
 *  2. Inline prompts are always allowed.
 *  3. Session ceiling: if any modal has shown this tab session → false.
 *  4. Once-ever check (cooldownDays === -1).
 *  5. Per-source cooldown window.
 *  6. Otherwise → true.
 */
export function canShowPrompt(source: string): boolean {
  const rule = PROMPT_RULES[source] ?? PROMPT_RULES["intent_postpayment_followup"];

  if (rule.style === "inline") {
    return true;
  }

  // Session ceiling (sessionStorage resets on tab close).
  if (getSessionModalCount() >= 1) {
    return false;
  }

  // Once-ever gate.
  if (rule.cooldownDays === -1) {
    if (lsGet<boolean>(everShownKey(source)) === true) {
      return false;
    }
  }

  // Per-source cooldown window.
  if (rule.cooldownDays > 0) {
    const lastShown = lsGet<number>(lastShownKey(source));
    if (typeof lastShown === "number") {
      const daysSince = (Date.now() - lastShown) / (1000 * 60 * 60 * 24);
      if (daysSince < rule.cooldownDays) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Call after the prompt for `source` has been shown (or submitted).
 *
 * Updates:
 *  - sessionStorage modal counter (modal prompts only).
 *  - localStorage last-shown timestamp.
 *  - localStorage ever-shown flag (once-ever sources).
 */
export function recordPromptShown(source: string): void {
  const rule = PROMPT_RULES[source] ?? PROMPT_RULES["intent_postpayment_followup"];

  if (rule.style === "modal") {
    incrementSessionModalCount();
  }

  lsSet(lastShownKey(source), Date.now());

  if (rule.cooldownDays === -1) {
    lsSet(everShownKey(source), true);
  }
}

// ---------------------------------------------------------------------------
// Dev / QA utility
// ---------------------------------------------------------------------------

/**
 * Resets all prompt ceiling state in sessionStorage AND localStorage.
 * Only available when `process.env.NODE_ENV !== "production"`.
 * Call from browser DevTools or a QA debug panel.
 *
 * @example
 *   import { __resetAllPromptState } from '@/lib/feedback/promptCeiling';
 *   __resetAllPromptState(); // in a dev debug action
 */
export function __resetAllPromptState(): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  safeSessionRemove(SESSION_MODALS_KEY);

  const allSources = Object.keys(PROMPT_RULES);
  for (const source of allSources) {
    lsSet(lastShownKey(source), null);
    lsSet(everShownKey(source), null);
  }
}
